import { useState } from 'react'
import { ArrowLeft, Eye, EyeOff, KeyRound, Mail, ShieldCheck } from 'lucide-react'
import { isPasswordRecoveryFlow, requestPasswordReset, resendConfirmation, signIn, signOut, updatePassword } from '../services/crm.js'
import { isSupabaseConfigured } from '../lib/supabase.js'

const friendlyAuthError = error => {
  const message = String(error?.message || '').toLowerCase()
  if (message.includes('email not confirmed')) return 'Confirme seu e-mail antes de entrar.'
  if (message.includes('invalid login credentials')) return 'E-mail ou senha incorretos.'
  if (message.includes('password should be')) return 'A senha deve ter pelo menos 8 caracteres.'
  if (message.includes('same password')) return 'Escolha uma senha diferente da atual.'
  if (message.includes('session missing') || message.includes('invalid token') || message.includes('expired')) return 'Este link é inválido ou expirou. Solicite um novo link de recuperação.'
  if (message.includes('rate limit')) return 'Muitas tentativas. Aguarde alguns minutos e tente novamente.'
  return error?.message || 'Não foi possível concluir a solicitação.'
}

export default function AuthScreen({ onAuthenticated, onRecoveryComplete }) {
  const [view, setView] = useState(isPasswordRecoveryFlow() ? 'reset' : 'login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  function changeView(next) {
    setView(next); setError(''); setSuccess(''); setPassword(''); setConfirmation('')
  }

  async function submitLogin(event) {
    event.preventDefault(); setBusy(true); setError(''); setSuccess('')
    try { await signIn(email.trim(), password); onAuthenticated() }
    catch (err) { setError(friendlyAuthError(err)) }
    finally { setBusy(false) }
  }

  async function submitRecovery(event) {
    event.preventDefault(); setBusy(true); setError(''); setSuccess('')
    try {
      await requestPasswordReset(email.trim())
      setSuccess('Se o e-mail estiver cadastrado, você receberá um link para criar uma nova senha.')
    } catch (err) { setError(friendlyAuthError(err)) }
    finally { setBusy(false) }
  }

  async function submitReset(event) {
    event.preventDefault(); setError(''); setSuccess('')
    if (password.length < 8) return setError('A senha deve ter pelo menos 8 caracteres.')
    if (password !== confirmation) return setError('As senhas informadas não coincidem.')
    setBusy(true)
    try {
      await updatePassword(password)
      await signOut()
      window.history.replaceState({}, '', window.location.pathname)
      setSuccess('Senha alterada. Entre novamente com a nova senha.')
      setView('login'); setPassword(''); setConfirmation('')
      onRecoveryComplete?.()
    } catch (err) { setError(friendlyAuthError(err)) }
    finally { setBusy(false) }
  }

  async function handleResend() {
    if (!email.trim()) return setError('Informe seu e-mail para reenviar a confirmação.')
    setBusy(true); setError(''); setSuccess('')
    try {
      await resendConfirmation(email.trim())
      setSuccess('Se houver uma confirmação pendente, enviaremos uma nova mensagem.')
    } catch (err) { setError(friendlyAuthError(err)) }
    finally { setBusy(false) }
  }

  const content = view === 'login'
    ? { icon: <ShieldCheck />, eyebrow: 'Ambiente comercial seguro', title: 'Acesse seu CRM', description: 'Organize oportunidades e acompanhe o time em um só lugar.' }
    : view === 'forgot'
      ? { icon: <Mail />, eyebrow: 'Recuperação de acesso', title: 'Esqueceu sua senha?', description: 'Informe seu e-mail e enviaremos um link seguro para você.' }
      : { icon: <KeyRound />, eyebrow: 'Nova senha', title: 'Crie uma nova senha', description: 'Use pelo menos 8 caracteres e não compartilhe sua senha.' }

  return <main className="login-page">
    <section className="login-card auth-card">
      <img src="/logo-dark.png" alt="Caminho da Venda" className="login-logo" />
      <div className="auth-heading"><span className="auth-symbol">{content.icon}</span><div><span className="eyebrow">{content.eyebrow}</span><h1>{content.title}</h1><p>{content.description}</p></div></div>
      {!isSupabaseConfigured && <div className="demo-notice">Modo demonstração ativo. Digite qualquer e-mail e senha para visualizar.</div>}
      {view === 'login' && <form onSubmit={submitLogin} className="login-form">
        <label>E-mail<input type="email" autoComplete="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="voce@empresa.com.br" required /></label>
        <label>Senha<div className="password-field"><input type={showPassword ? 'text' : 'password'} autoComplete="current-password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Sua senha" required /><button type="button" onClick={() => setShowPassword(value => !value)} aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}>{showPassword ? <EyeOff /> : <Eye />}</button></div></label>
        {error && <div className="auth-message error" role="alert">{error}{error.includes('Confirme seu e-mail') && <button type="button" onClick={handleResend}>Reenviar confirmação</button>}</div>}
        {success && <div className="auth-message success" role="status">{success}</div>}
        <button className="primary wide" disabled={busy}>{busy ? 'Entrando…' : 'Entrar'}</button>
        <button type="button" className="auth-link" onClick={() => changeView('forgot')}>Esqueci minha senha</button>
      </form>}
      {view === 'forgot' && <form onSubmit={submitRecovery} className="login-form">
        <label>E-mail<input type="email" autoComplete="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="voce@empresa.com.br" required /></label>
        {error && <div className="auth-message error" role="alert">{error}</div>}
        {success && <div className="auth-message success" role="status">{success}</div>}
        <button className="primary wide" disabled={busy}>{busy ? 'Enviando…' : 'Enviar link de recuperação'}</button>
        <button type="button" className="auth-link" onClick={() => changeView('login')}><ArrowLeft /> Voltar para o login</button>
      </form>}
      {view === 'reset' && <form onSubmit={submitReset} className="login-form">
        <label>Nova senha<div className="password-field"><input type={showPassword ? 'text' : 'password'} autoComplete="new-password" minLength="8" value={password} onChange={e => setPassword(e.target.value)} required /><button type="button" onClick={() => setShowPassword(value => !value)} aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}>{showPassword ? <EyeOff /> : <Eye />}</button></div></label>
        <label>Confirmar nova senha<input type="password" autoComplete="new-password" minLength="8" value={confirmation} onChange={e => setConfirmation(e.target.value)} required /></label>
        {error && <div className="auth-message error" role="alert">{error}</div>}
        <button className="primary wide" disabled={busy}>{busy ? 'Salvando…' : 'Salvar nova senha'}</button>
        <button type="button" className="auth-link" onClick={() => changeView('login')}><ArrowLeft /> Voltar para o login</button>
      </form>}
      <p className="auth-support">Seu acesso é individual e protegido. Nunca compartilhe sua senha.</p>
    </section>
  </main>
}
